import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { apiGet, apiPatch } from "../../api/client";
import type { OrgUsageSpendData, SuperOrgAiPanel } from "./superOrgTypes";

export function OrgUsageSpendPanel({
  orgId,
  token,
  compact,
}: {
  orgId: string;
  token: string;
  /** En pestaña dedicada, sin regla superior */
  compact?: boolean;
}) {
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState<OrgUsageSpendData | null>(null);
  const [loading, setLoading] = useState(false);
  const [localErr, setLocalErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLocalErr('');
    try {
      const fromIso = new Date(from + 'T00:00:00.000Z').toISOString();
      const toIso = new Date(to + 'T23:59:59.999Z').toISOString();
      const d = await apiGet<OrgUsageSpendData>(
        `/api/super/organizations/${orgId}/usage-spend?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`,
        token
      );
      setData(d);
    } catch (e) {
      setLocalErr((e as Error).message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [orgId, token, from, to]);

  useEffect(() => {
    void load();
    // Carga inicial o al cambiar de empresa; las fechas se aplican con "Actualizar".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, token]);

  return (
    <section
      style={{
        marginTop: compact ? 0 : 20,
        paddingTop: compact ? 0 : 16,
        borderTop: compact ? 'none' : '1px solid #e2e8f0',
      }}
    >
      <h3 style={{ fontSize: 14, margin: '0 0 8px' }}>Uso de tokens y costo aproximado (USD)</h3>
      <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px', lineHeight: 1.5 }}>
        Agrupación por <strong>modelo de API</strong> (<code>agentModel</code> o <code>modelId</code> de borrador).
        El <strong>coste</strong> se desglosa: respuestas en <strong>conversación</strong> frente a
        <strong> borradores (LLM) </strong>
        en Aprendizajes (sin mensaje de hilo). Si el catálogo no
        tiene USD/1M, se usan tarifas de referencia. No reemplaza la factura del proveedor.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 12 }}>
        <label style={f}>
          Desde
          <input style={inp} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label style={f}>
          Hasta
          <input style={inp} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <button type="button" style={btnOutline} onClick={() => void load()} disabled={loading}>
          {loading ? 'Calculando…' : 'Actualizar'}
        </button>
      </div>
      {localErr ? <p style={{ color: '#b91c1c', fontSize: 13 }}>{localErr}</p> : null}
      {data ? (
        <>
          <p style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 6px' }}>
            Total estimado:{' '}
            <span style={{ color: '#16a34a' }}>
              US$ {data.totalEstimatedUsd.toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
            </span>
          </p>
          <p style={{ fontSize: 12, color: '#475569', margin: '0 0 10px', lineHeight: 1.5 }}>
            Desglose: <strong>conversación (mensajes del agente)</strong> US${' '}
            {data.totalEstimatedFromConversationUsd.toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ·{' '}
            <strong>aprendizajes, borradores con LLM</strong> US${' '}
            {data.totalEstimatedFromLearningsDraftLlmUsd.toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
          </p>
          <p style={{ fontSize: 11, color: '#94a3b8', margin: '0 0 10px', lineHeight: 1.45 }}>{data.note}</p>
          {data.modelsWithUsageWithoutPrice.length > 0 ? (
            <p style={{ fontSize: 12, color: '#b45309', margin: '0 0 8px' }}>
              Sin precio en catálogo o modelo compuesto: {data.modelsWithUsageWithoutPrice.join(', ')}
            </p>
          ) : null}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>
                  <th style={{ padding: '6px 8px' }}>agentModel (guardado)</th>
                  <th style={{ padding: '6px 8px' }} title="Mensajes del agente en conversación">Msgs (chat)</th>
                  <th style={{ padding: '6px 8px' }} title="Llamadas Borrador (LLM) en Aprendizajes">Borr. LLM</th>
                  <th style={{ padding: '6px 8px' }}>Entr. (t)</th>
                  <th style={{ padding: '6px 8px' }}>Sal. (t)</th>
                  <th style={{ padding: '6px 8px' }}>USD/1M in</th>
                  <th style={{ padding: '6px 8px' }}>USD/1M out</th>
                  <th style={{ padding: '6px 8px' }}>~ USD chat</th>
                  <th style={{ padding: '6px 8px' }}>~ USD aprend.</th>
                  <th style={{ padding: '6px 8px' }}>~ USD total</th>
                </tr>
              </thead>
              <tbody>
                {data.byModel.map((row) => (
                  <tr key={row.agentModel} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 8px', fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>{row.agentModel}</td>
                    <td style={{ padding: '6px 8px' }}>{row.fromConversation.messageCount.toLocaleString('es-UY')}</td>
                    <td style={{ padding: '6px 8px' }}>{row.fromLearningsDraftLlm.callCount.toLocaleString('es-UY')}</td>
                    <td style={{ padding: '6px 8px' }}>{row.promptSum.toLocaleString('es-UY')}</td>
                    <td style={{ padding: '6px 8px' }}>{row.completionSum.toLocaleString('es-UY')}</td>
                    <td style={{ padding: '6px 8px' }}>{row.inputUsdPerMillion ?? '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{row.outputUsdPerMillion ?? '—'}</td>
                    <td style={{ padding: '6px 8px' }}>
                      {row.fromConversation.estimatedUsd != null
                        ? `US$ ${row.fromConversation.estimatedUsd.toLocaleString('es-UY', { maximumFractionDigits: 4 })}`
                        : '—'}
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      {row.fromLearningsDraftLlm.estimatedUsd != null
                        ? `US$ ${row.fromLearningsDraftLlm.estimatedUsd.toLocaleString('es-UY', { maximumFractionDigits: 4 })}`
                        : '—'}
                    </td>
                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>
                      {row.estimatedUsd != null
                        ? `US$ ${row.estimatedUsd.toLocaleString('es-UY', { maximumFractionDigits: 4 })}`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.byModel.length === 0 ? <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 8 }}>Sin datos en el período.</p> : null}
          </div>
        </>
      ) : !loading && !localErr ? (
        <p style={{ color: '#94a3b8', fontSize: 13 }}>Sin datos.</p>
      ) : null}
    </section>
  );
}

function providerLabelForModelId(modelId: string): string {
  const m = modelId.trim().toLowerCase();
  if (m.startsWith('claude-')) return 'Anthropic';
  if (m.startsWith('deepseek-')) return 'DeepSeek';
  return 'OpenAI';
}

function groupCatalogByProvider<T extends { modelId: string }>(items: T[]): { title: string; items: T[] }[] {
  const isClaude = (p: { modelId: string }) => p.modelId.trim().toLowerCase().startsWith('claude-');
  const isDeepseek = (p: { modelId: string }) => p.modelId.trim().toLowerCase().startsWith('deepseek-');
  const openai = items.filter((p) => !isClaude(p) && !isDeepseek(p));
  const anthropic = items.filter(isClaude);
  const deepseek = items.filter(isDeepseek);
  const out: { title: string; items: T[] }[] = [];
  if (openai.length) out.push({ title: 'OpenAI', items: openai });
  if (anthropic.length) out.push({ title: 'Anthropic (Claude)', items: anthropic });
  if (deepseek.length) out.push({ title: 'DeepSeek', items: deepseek });
  return out;
}

export function OrgAiPanel({
  orgId,
  token,
  ai,
  onUpdated,
  onError,
  compact,
}: {
  orgId: string;
  token: string;
  ai: SuperOrgAiPanel;
  onUpdated: (next: SuperOrgAiPanel) => void;
  onError: (msg: string) => void;
  compact?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [defaultModalOpen, setDefaultModalOpen] = useState(false);
  const [modalPresetPick, setModalPresetPick] = useState<Set<string>>(() => new Set());
  const [defInherit, setDefInherit] = useState(!ai.orgDefaultChatModel?.trim());
  const [defDraft, setDefDraft] = useState(ai.orgDefaultChatModel?.trim() ?? '');
  const [keyOpenai, setKeyOpenai] = useState('');
  const [keyAnthropic, setKeyAnthropic] = useState('');
  const [keyDeepseek, setKeyDeepseek] = useState('');

  const catalogUnrestricted = ai.allowedPresetIds.length === 0;
  const allowedSet = new Set(ai.allowedPresetIds);
  const allowedRows = catalogUnrestricted
    ? []
    : ai.presetCatalog.filter((p) => allowedSet.has(p.presetId));
  const hasDeepseekInCatalog = ai.presetCatalog.some((p) => p.modelId.trim().toLowerCase().startsWith('deepseek-'));

  useEffect(() => {
    const hasO = Boolean(ai.orgDefaultChatModel?.trim());
    setDefInherit(!hasO);
    setDefDraft(ai.orgDefaultChatModel?.trim() ?? '');
  }, [orgId, ai]);

  useEffect(() => {
    setKeyOpenai('');
    setKeyAnthropic('');
    setKeyDeepseek('');
  }, [orgId]);

  const patchOrgAi = async (payload: Record<string, unknown>, successMessage = 'Configuración IA guardada'): Promise<boolean> => {
    setBusy(true);
    onError('');
    try {
      const next = await apiPatch<SuperOrgAiPanel>(`/api/super/organizations/${orgId}/ai`, payload, token, {
        successMessage,
      });
      onUpdated(next);
      return true;
    } catch (ex) {
      onError((ex as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveNewApiKeys = async () => {
    const payload: Record<string, unknown> = {};
    if (keyOpenai.trim()) payload.openaiApiKey = keyOpenai.trim();
    if (keyAnthropic.trim()) payload.anthropicApiKey = keyAnthropic.trim();
    if (keyDeepseek.trim()) payload.deepseekApiKey = keyDeepseek.trim();
    if (Object.keys(payload).length === 0) {
      onError('Escribí al menos una clave nueva o usá "Heredar" en el proveedor que quieras quitar.');
      return;
    }
    const ok = await patchOrgAi(payload, 'Claves de API de la empresa actualizadas');
    if (ok) {
      setKeyOpenai('');
      setKeyAnthropic('');
      setKeyDeepseek('');
    }
  };

  const clearOrgKey = async (which: 'openai' | 'anthropic' | 'deepseek') => {
    const k =
      which === 'openai'
        ? { openaiApiKey: null }
        : which === 'anthropic'
          ? { anthropicApiKey: null }
          : { deepseekApiKey: null };
    const msg =
      which === 'openai'
        ? 'Clave de OpenAI: la empresa vuelve a heredar (plataforma o entorno).'
        : which === 'anthropic'
          ? 'Clave de Anthropic: la empresa vuelve a heredar (plataforma o entorno).'
          : 'Clave de DeepSeek: la empresa vuelve a heredar (plataforma o entorno).';
    if (!window.confirm('¿Quitar la clave guardada en esta empresa y heredar de la plataforma o del servidor?')) return;
    await patchOrgAi(k, msg);
  };

  const openAddModal = () => {
    setModalPresetPick(new Set());
    setAddModalOpen(true);
    onError('');
  };

  const toggleModalPreset = (presetId: string) => {
    setModalPresetPick((prev) => {
      const n = new Set(prev);
      if (n.has(presetId)) n.delete(presetId);
      else n.add(presetId);
      return n;
    });
  };

  const selectAllModalPresets = () => {
    setModalPresetPick(new Set(ai.presetCatalog.map((p) => p.presetId)));
  };

  const clearModalPresets = () => setModalPresetPick(new Set());

  const saveAddModal = async () => {
    if (modalPresetPick.size === 0) {
      onError('Marcá al menos un modelo del catálogo (OpenAI, Anthropic o DeepSeek).');
      return;
    }
    const payload: Record<string, unknown> = {};
    if (catalogUnrestricted) {
      payload.allowedPresetIds = [...modalPresetPick];
    } else {
      payload.allowedPresetIds = [...new Set([...ai.allowedPresetIds, ...modalPresetPick])];
    }
    const ok = await patchOrgAi(payload);
    if (ok) {
      setAddModalOpen(false);
      setModalPresetPick(new Set());
    }
  };

  const removePresetRow = async (presetId: string) => {
    const next = ai.allowedPresetIds.filter((id) => id !== presetId);
    const msg =
      next.length === 0
        ? 'Al quitar el último modelo se habilitará de nuevo todo el catálogo para esta empresa. ¿Continuar?'
        : '¿Quitar este modelo de la lista permitida?';
    if (!window.confirm(msg)) return;
    await patchOrgAi({ allowedPresetIds: next });
  };

  const allowFullCatalog = async () => {
    if (
      !window.confirm(
        '¿Habilitar todo el catálogo global? Se borrará la lista restringida de modelos para esta empresa.'
      )
    )
      return;
    await patchOrgAi({ allowedPresetIds: [] });
  };

  const openDefaultModal = () => {
    const hasO = Boolean(ai.orgDefaultChatModel?.trim());
    setDefInherit(!hasO);
    setDefDraft(ai.orgDefaultChatModel?.trim() ?? '');
    setDefaultModalOpen(true);
    onError('');
  };

  const saveDefaultModal = async () => {
    const ok = await patchOrgAi({
      defaultChatModel: defInherit ? null : defDraft.trim() || null,
    });
    if (ok) setDefaultModalOpen(false);
  };

  return (
    <section
      style={{
        marginTop: compact ? 0 : 20,
        padding: 16,
        background: '#f8fafc',
        borderRadius: 10,
        border: '1px solid #e2e8f0',
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <h3 style={{ fontSize: 15, margin: '0 0 6px', color: '#0f172a' }}>Modelos IA de esta empresa</h3>
          <p style={{ fontSize: 12, color: '#64748b', margin: 0, lineHeight: 1.5, maxWidth: 600 }}>
            Elegí qué modelos del catálogo global (<Link to="/super/ai">Modelos IA (plataforma)</Link>) puede usar la
            consola de la empresa, y abajo (opcional) <strong>claves API propias</strong> por proveedor. Si no cargás
            clave para un proveedor, se usan las de plataforma o las variables del servidor (p. ej.{' '}
            <code>OPENAI_API_KEY</code>). Las claves se guardan cifradas en esta organización.
          </p>
        </div>
        <button type="button" style={btn} disabled={busy} onClick={() => void openAddModal()}>
          + Añadir modelos
        </button>
      </div>

      <h4 style={{ fontSize: 13, margin: '20px 0 10px', color: '#334155' }}>Claves API por proveedor (empresa)</h4>
      <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px', lineHeight: 1.5, maxWidth: 640 }}>
        Una clave por proveedor (no por cada <code>modelId</code>). Nuevo valor: pegá y guardá. Heredar: quitá la clave
        guardada en la empresa. Los modelos con prefijo <code>gpt-</code> usan OpenAI; <code>claude-</code> Anthropic;{' '}
        <code>deepseek-</code> DeepSeek.
      </p>
      {hasDeepseekInCatalog ? null : (
        <p style={{ fontSize: 12, color: '#b45309', margin: '0 0 12px', lineHeight: 1.5, maxWidth: 640, padding: 10, background: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a' }}>
          <strong>¿No ves DeepSeek en el catálogo al añadir modelos?</strong> Ingresá a{' '}
          <Link to="/super/ai">Modelos IA (plataforma)</Link> y pulsá <strong>Sincronizar catálogo y precios</strong> (o
          añadí a mano <code>deepseek-chat</code> / <code>deepseek-reasoner</code>).
        </p>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0,1fr)',
          gap: 12,
          marginBottom: 8,
        }}
      >
        {(
          [
            { id: 'openai' as const, label: 'OpenAI', hint: ai.openaiKeyHint, on: ai.openaiKeyConfigured, v: keyOpenai, set: setKeyOpenai },
            { id: 'anthropic' as const, label: 'Anthropic', hint: ai.anthropicKeyHint, on: ai.anthropicKeyConfigured, v: keyAnthropic, set: setKeyAnthropic },
            { id: 'deepseek' as const, label: 'DeepSeek', hint: ai.deepseekKeyHint, on: ai.deepseekKeyConfigured, v: keyDeepseek, set: setKeyDeepseek },
          ] as const
        ).map((row) => (
          <div
            key={row.id}
            style={{
              padding: 12,
              background: '#fff',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{row.label}</span>
              <span style={{ fontSize: 12, color: row.on ? '#16a34a' : '#94a3b8' }}>{row.on ? `Guardada: ${row.hint ?? 'sí'}` : 'Hereda de plataforma o env'}</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <input
                type="password"
                style={{ ...inp, flex: '1 1 220px', minWidth: 0 }}
                value={row.v}
                onChange={(e) => row.set(e.target.value)}
                placeholder="Pegar nueva clave (opcional)"
                autoComplete="off"
              />
              {row.on ? (
                <button type="button" style={btnGhostSm} disabled={busy} onClick={() => void clearOrgKey(row.id)}>
                  Heredar (quitar)
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 6 }}>
        <button type="button" style={btn} disabled={busy} onClick={() => void saveNewApiKeys()}>
          {busy ? 'Guardando…' : 'Guardar claves nuevas'}
        </button>
      </div>

      <h4 style={{ fontSize: 13, margin: '18px 0 8px', color: '#334155' }}>Modelos permitidos en la consola</h4>
      {ai.presetCatalog.length === 0 ? (
        <p style={{ fontSize: 12, color: '#b45309', margin: '0 0 8px' }}>
          No hay modelos en el catálogo global. Creálos primero en <Link to="/super/ai">Modelos IA</Link>.
        </p>
      ) : catalogUnrestricted ? (
        <div
          style={{
            padding: 12,
            background: '#fff',
            borderRadius: 8,
            border: '1px solid #e2e8f0',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 12,
            justifyContent: 'space-between',
          }}
        >
          <span style={{ fontSize: 13, color: '#475569' }}>
            <strong>Todo el catálogo</strong> ({ai.presetCatalog.length} modelos) — sin restricción por empresa.
          </span>
          <button type="button" style={btnOutline} disabled={busy} onClick={() => void openAddModal()}>
            Restringir u añadir…
          </button>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', background: '#f1f5f9', color: '#64748b' }}>
                <th style={{ padding: '10px 12px' }}>Etiqueta</th>
                <th style={{ padding: '10px 12px' }}>Model ID</th>
                <th style={{ padding: '10px 12px' }}>Proveedor</th>
                <th style={{ padding: '10px 12px', width: 88 }} />
              </tr>
            </thead>
            <tbody>
              {allowedRows.map((p) => (
                <tr key={p.presetId} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 12px' }}>{p.label}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <code style={{ fontSize: 11, background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>
                      {p.modelId}
                    </code>
                  </td>
                  <td style={{ padding: '10px 12px' }}>{providerLabelForModelId(p.modelId)}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <button
                      type="button"
                      style={btnDangerSm}
                      disabled={busy}
                      onClick={() => void removePresetRow(p.presetId)}
                    >
                      Quitar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: 10, borderTop: '1px solid #f1f5f9', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button type="button" style={btnOutline} disabled={busy} onClick={() => void openAddModal()}>
              + Añadir más modelos
            </button>
            <button type="button" style={btnGhostSm} disabled={busy} onClick={() => void allowFullCatalog()}>
              Usar todo el catálogo
            </button>
          </div>
        </div>
      )}

      <h4 style={{ fontSize: 13, margin: '18px 0 8px', color: '#334155' }}>Modelo por defecto (sin agente / fallback)</h4>
      <div
        style={{
          padding: 12,
          background: '#fff',
          borderRadius: 8,
          border: '1px solid #e2e8f0',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 12,
          justifyContent: 'space-between',
        }}
      >
        <div style={{ fontSize: 13, color: '#475569' }}>
          {ai.orgDefaultChatModel?.trim() ? (
            <>
              Empresa: <code>{ai.orgDefaultChatModel}</code>
            </>
          ) : (
            <>
              Hereda de plataforma: <code>{ai.platformDefaultChatModel}</code>
            </>
          )}
        </div>
        <button type="button" style={btnOutline} disabled={busy} onClick={() => openDefaultModal()}>
          Editar
        </button>
      </div>

      {addModalOpen ? (
        <div
          style={modalBackdrop}
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setAddModalOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="org-ai-add-title"
            style={modalBox}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="org-ai-add-title" style={{ margin: '0 0 6px', fontSize: 17, color: '#0f172a' }}>
              Añadir modelos al catálogo permitido
            </h4>
            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px', lineHeight: 1.5 }}>
              Marcá los modelos de OpenAI, Anthropic y/o DeepSeek que esta empresa podrá elegir en sus agentes. Si antes
              tenía todo el catálogo, al guardar la lista quedará limitada a lo que marques aquí; si ya estaba restringida,
              se suman
              los nuevos.
            </p>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Catálogo global</span>
              <span style={{ display: 'flex', gap: 10 }}>
                <button type="button" style={btnLink} onClick={selectAllModalPresets}>
                  Marcar todos
                </button>
                <button type="button" style={btnLink} onClick={clearModalPresets}>
                  Ninguno
                </button>
              </span>
            </div>
            <div
              style={{
                maxHeight: 280,
                overflowY: 'auto',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: 8,
                background: '#fafafa',
                marginBottom: 16,
              }}
            >
              {groupCatalogByProvider(ai.presetCatalog).map((group) => (
                <div key={group.title} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8, letterSpacing: '0.02em' }}>
                    {group.title}
                  </div>
                  {group.items.map((p) => (
                    <label
                      key={p.presetId}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        padding: '8px 6px',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: 13,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={modalPresetPick.has(p.presetId)}
                        onChange={() => toggleModalPreset(p.presetId)}
                      />
                      <span>
                        <strong style={{ color: '#1e293b' }}>{p.label}</strong>
                        <br />
                        <code style={{ fontSize: 11, background: '#e2e8f0', padding: '1px 6px', borderRadius: 4 }}>
                          {p.modelId}
                        </code>
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" style={btnOutline} disabled={busy} onClick={() => setAddModalOpen(false)}>
                Cancelar
              </button>
              <button type="button" style={btn} disabled={busy} onClick={() => void saveAddModal()}>
                {busy ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {defaultModalOpen ? (
        <div
          style={modalBackdrop}
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setDefaultModalOpen(false);
          }}
        >
          <div role="dialog" aria-modal="true" style={modalBox} onClick={(e) => e.stopPropagation()}>
            <h4 style={{ margin: '0 0 12px', fontSize: 17, color: '#0f172a' }}>Modelo por defecto</h4>
            <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px' }}>
              Plataforma: <code>{ai.platformDefaultChatModel}</code>
            </p>
            <label style={{ ...f, flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <input
                type="checkbox"
                checked={defInherit}
                onChange={(e) => {
                  setDefInherit(e.target.checked);
                  if (e.target.checked) setDefDraft('');
                }}
              />
              Heredar de la plataforma
            </label>
            {!defInherit ? (
              <input
                style={{ ...inp, width: '100%', boxSizing: 'border-box', marginBottom: 16 }}
                value={defDraft}
                onChange={(e) => setDefDraft(e.target.value)}
                placeholder="gpt-4o-mini o claude-…"
              />
            ) : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" style={btnOutline} disabled={busy} onClick={() => setDefaultModalOpen(false)}>
                Cancelar
              </button>
              <button type="button" style={btn} disabled={busy} onClick={() => void saveDefaultModal()}>
                {busy ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

const modalBackdrop: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.45)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  overflowY: 'auto',
};

const modalBox: CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  padding: 22,
  maxWidth: 480,
  width: '100%',
  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
  maxHeight: '90vh',
  overflowY: 'auto',
};

const btnDangerSm: CSSProperties = {
  padding: '5px 10px',
  borderRadius: 6,
  border: '1px solid #fecaca',
  background: '#fff',
  color: '#b91c1c',
  fontSize: 12,
  cursor: 'pointer',
};

const btnGhostSm: CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: '#475569',
  fontSize: 12,
  cursor: 'pointer',
};

const btnLink: CSSProperties = {
  border: 'none',
  background: 'none',
  color: '#7c3aed',
  fontSize: 12,
  cursor: 'pointer',
  padding: 0,
  textDecoration: 'underline',
};

export const superOrgField: CSSProperties = { display: 'flex', flexDirection: 'column', fontSize: 12, color: '#64748b' };
export const superOrgInput: CSSProperties = {
  marginTop: 4,
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid #cbd5e1',
  minWidth: 180,
};
export const superOrgBtn: CSSProperties = {
  padding: '8px 16px',
  borderRadius: 8,
  border: 'none',
  background: '#7c3aed',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
};
export const superOrgBtnOutline: CSSProperties = {
  ...superOrgBtn,
  background: '#fff',
  color: '#7c3aed',
  border: '1px solid #ddd6fe',
};

const f = superOrgField;
const inp = superOrgInput;
const btn = superOrgBtn;
const btnOutline = superOrgBtnOutline;
